import React from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ConfirmationModal } from "../../../Common/Components/ConfirmationModal";
import { baseUrl } from "../../../Services/Axios";
import { transformOrderDetailItems } from "../../../Services/MainService";
import { cancelOrder, fetchOrderDetails, trackOrder } from "../../../Services/OrderService";
import {
  HelpCard,
  OrderNotes,
  OrderOverview,
  OrderProgress,
  PageHeading,
  PriceSummary,
  RiderDetails,
  ScheduledDates,
  ServiceAccordions,
} from "./OrderDetailsSections";
import { buildTrack, parseNotes } from "./helpers";
import "./OrderDetailsRevamp.css";

const trackPurchase = (data) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "purchase",
    transaction_id: data.id,
    affiliation: "Jab Chaho Online Store",
    value: data.order_amount,
    tax: data.tax_amount || 0,
    shipping: data.delivery_charge || 0,
    currency: "PKR",
    coupon: data.coupon_code || "",
    items: (data.details || []).map((item) => ({
      item_id: item.laundry_item?.id,
      item_name: item.laundry_item?.name,
      price: item.price,
      quantity: item.quantity,
    })),
  });
};

export const OrderDetailsRevamp = ({
  title = "Order Details",
  subtitle = "Here's everything you need to know about your order.",
}) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const config = useSelector((state) => state.Config);
  const customer = useSelector((state) => state.Customer);
  const [loading, setLoading] = React.useState(true);
  const [details, setDetails] = React.useState(null);
  const [services, setServices] = React.useState([]);
  const [open, setOpen] = React.useState({});
  const [count, setCount] = React.useState(0);
  const [itemPrice, setItemPrice] = React.useState(0);
  const [track, setTrack] = React.useState([]);
  const [trackLoading, setTrackLoading] = React.useState(false);
  const [showTrack, setShowTrack] = React.useState(false);
  const [showCancel, setShowCancel] = React.useState(false);
  const [cancelLoading, setCancelLoading] = React.useState(false);

  React.useEffect(() => {
    if (!id) {
      navigate(-1, { replace: true });
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    fetchOrderDetails(id).then(
      ({ data }) => {
        if (!mounted) return;
        const transformed = transformOrderDetailItems(data.details || []);
        setServices(transformed.record);
        setOpen(transformed.open);
        setCount(transformed.count);
        setItemPrice(transformed.items_price);
        setTrack(buildTrack(data));
        setDetails(data);
        trackPurchase(data);
        setLoading(false);
      },
      () => {
        if (!mounted) return;
        setLoading(false);
        toast.error("Something went wrong while fetching order details.");
      }
    );
    return () => { mounted = false; };
  }, [id, location.key, navigate]);

  const openTracking = () => {
    setShowTrack(true);
    setTrackLoading(true);
    trackOrder(details.id).then(
      ({ data }) => { setTrack(buildTrack(data)); setTrackLoading(false); },
      () => { setTrackLoading(false); toast.error("Something went wrong while fetching tracking."); }
    );
  };

  const confirmCancellation = () => {
    if (cancelLoading) return;
    setCancelLoading(true);
    cancelOrder(details.id).then(
      () => { toast.success("Order cancelled successfully!"); navigate(`/app/order-canceled/${details.id}`, { replace: true }); },
      () => { setCancelLoading(false); toast.error("Something went wrong while cancelling the order."); }
    );
  };

  const printReceipt = () => {
    const previousOpen = open;
    const expandedServices = services.reduce(
      (result, service) => ({ ...result, [service.services_id]: true }),
      {}
    );
    setOpen(expandedServices);
    window.setTimeout(() => {
      const restoreAccordions = () => {
        setOpen(previousOpen);
        window.removeEventListener("afterprint", restoreAccordions);
      };
      window.addEventListener("afterprint", restoreAccordions);
      window.print();
    }, 100);
  };

  if (loading) return <div className="odr-loading"><Spinner variant="warning" /></div>;
  if (!details) return <div className="odr-loading">Order details could not be loaded.</div>;

  const delivered = Boolean(details.delivered || details.order_status === "delivered");
  const cancellable = ["pending", "schedule"].includes(details.order_status);
  const person = `${customer?.f_name || ""} ${customer?.l_name || ""}`.trim();
  const query = `order_id=${details.id}&name=${encodeURIComponent(person)}&email=${encodeURIComponent(customer?.email || "")}&mobile_number=${encodeURIComponent(customer?.phone || "")}`;

  return (
    <main className="odr-page">
      <div className="odr-container">
        <PageHeading title={title} subtitle={subtitle} details={details} />
        <OrderOverview details={details} />
        <OrderProgress details={details} onOpenTracking={openTracking} />
        <div className="odr-content">
          <ServiceAccordions services={services} open={open} onToggle={(serviceId) => setOpen((current) => ({ ...current, [serviceId]: !current[serviceId] }))} />
          <aside className="odr-sidebar">
            <PriceSummary count={count} itemPrice={itemPrice} details={details} />
            <ScheduledDates details={details} />
            <OrderNotes notes={parseNotes(details.note)} />
            <RiderDetails rider={details.rider} imageBaseUrl={config?.base_urls?.delivery_man_image_url} />
          </aside>
        </div>
        <HelpCard />
        {delivered && <div className="odr-review-actions"><a href={`${baseUrl}/order-reviews-ratings?${query}`} target="_blank" rel="noreferrer">Rate / Review</a><a href={`${baseUrl}/complaint-form?${query}`} target="_blank" rel="noreferrer">Register Complaint</a></div>}
        <div className="odr-actions">
          <button type="button" onClick={printReceipt}><i className="fa-solid fa-download" />Download Receipt</button>
          {cancellable && <button type="button" className="danger" onClick={() => setShowCancel(true)} disabled={cancelLoading}><i className="fa-regular fa-circle-xmark" />{cancelLoading ? "Cancelling..." : "Cancel Order"}</button>}
        </div>
      </div>

      <Modal show={showTrack} centered onHide={() => setShowTrack(false)} style={{ zIndex: 1056 }}>
        <Modal.Header closeButton><Modal.Title>Order Status</Modal.Title></Modal.Header>
        <Modal.Body>{trackLoading ? <div className="text-center py-4"><Spinner variant="warning" /></div> : track.length ? <ul className="odr-track">{track.map((entry, index) => <li key={`${entry.status}-${index}`}><span /><div><strong>{entry.status}</strong><small>{entry.time}, {entry.date}</small></div></li>)}</ul> : <p className="text-center text-muted py-4 mb-0">No tracking updates found.</p>}</Modal.Body>
      </Modal>
      <ConfirmationModal show={showCancel} text="Are you sure you want to cancel this order?" onNo={() => setShowCancel(false)} onYes={() => { setShowCancel(false); confirmCancellation(); }} />
    </main>
  );
};

export default OrderDetailsRevamp;
